import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, ArrowLeft, CalendarDays, RefreshCw, ClipboardCopy, Info, Mail } from 'lucide-react';
import { getDashboardDates, getDashboardReport, sendBugReportEmail } from '../services/api';

const WORKFLOW_SECTIONS = [
  { key: 'Open', label: 'Open', color: '#1976d2', description: 'Defect assigned for investigation and resolution.' },
  { key: 'Blocked', label: 'Blocked', color: '#c62828', description: 'Progress is pending due to a dependency or external issue.' },
  { key: 'Monitoring', label: 'Monitoring', color: '#f57c00', description: 'Fix is validated and being observed for stability.' },
  { key: 'Deferred', label: 'Deferred', color: '#7b1fa2', description: 'Defect acknowledged and planned for a future milestone or release.' },
  { key: 'Ready for QA', label: 'Ready for QA', color: '#2e7d32', description: 'Fix is available for QA validation.' },
];

const PRIORITY_ORDER = { 'Blocker': 0, 'High': 1, 'Medium': 2, 'Low': 3, 'Unset': 4 };
const TASKEI_URL = 'https://taskei.amazon.dev/tasks';

/** Escape HTML special characters to prevent XSS */
function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function today() { return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date()); }

/** Filter milestone value to only show M-values (M3, M4, etc.), exclude R-values */
function filterMilestone(value) {
  if (!value) return '—';
  const parts = value.split(',').map(s => s.trim()).filter(s => /^M\d+$/.test(s));
  return parts.length > 0 ? parts.join(', ') : '—';
}

function BugTable({ bugs, workflowLabel, color, description, owner, nextAction }) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="bug-table-section">
      <div className="bug-table-header">
        <h3 className="bug-table-title" style={{ borderLeftColor: color }}>
          {workflowLabel} <span className="bug-table-count">({bugs?.length || 0})</span>
        </h3>
        <button className="bug-table-info-btn" onClick={() => setShowInfo(!showInfo)} title="Show workflow definition"><Info size={14} /></button>
      </div>
      {showInfo && (
        <div className="bug-table-info-panel">
          <div>{description}</div>
        </div>
      )}
      {(!bugs || bugs.length === 0) ? (
        <p className="bug-table-empty">No bugs in this workflow.</p>
      ) : (
        <div className="bug-table-wrapper">
          <table className="bug-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Priority</th>
                <th>Module</th>
                <th>Assigned</th>
                <th>Teams</th>
                <th>Found in Milestone</th>
                <th>Targeted Milestone</th>
                <th>Lead</th>
              </tr>
            </thead>
            <tbody>
              {bugs.map((bug) => (
                <tr key={bug.shortId} className={`bug-row bug-row-${(bug.priority || 'unset').toLowerCase()}`}>
                  <td className="bug-title-cell">
                    <a href={`${TASKEI_URL}/${bug.shortId}`} target="_blank" rel="noopener noreferrer">
                      {bug.title}
                    </a>
                  </td>
                  <td><span className={`bug-priority-badge priority-${(bug.priority || 'unset').toLowerCase()}`}>{bug.priority}</span></td>
                  <td>{bug.module || '—'}</td>
                  <td>{bug.assigned || '—'}</td>
                  <td>{bug.teams || '—'}</td>
                  <td>{filterMilestone(bug.affectedMilestone)}</td>
                  <td>{filterMilestone(bug.targetedRelease)}</td>
                  <td>{bug.leadDays ? `${bug.leadDays}d` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BugDashboard() {
  const navigate = useNavigate();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null);

  useEffect(() => {
    getDashboardDates().then(dates => {
      setAvailableDates(dates || []);
      if (dates && dates.length > 0 && !dates.includes(selectedDate)) {
        setSelectedDate(dates[0]);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    getDashboardReport(selectedDate, '72', '')
      .then(data => setReport(data))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [selectedDate, refreshKey]);

  const bugsByWorkflow = useMemo(() => {
    if (!report?.bugDetails) return {};
    const grouped = {};
    for (const wf of WORKFLOW_SECTIONS) {
      grouped[wf.key] = [];
    }

    for (const bug of report.bugDetails) {
      const wf = bug.workflowStatus || 'Open';
      const matchedSection = WORKFLOW_SECTIONS.find(s => s.key.toLowerCase() === wf.toLowerCase());
      if (matchedSection) {
        grouped[matchedSection.key].push(bug);
      } else {
        grouped['Open'].push(bug);
      }
    }

    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
    }

    return grouped;
  }, [report]);

  const handleSendEmailReport = async () => {
    if (!report?.available) return;
    setSendingEmail(true);
    setEmailFeedback(null);
    try {
      const htmlBody = buildDashboardHtml();
      const allBugs = report.bugDetails || [];
      const result = await sendBugReportEmail(selectedDate, htmlBody, allBugs.length);
      setEmailFeedback({ type: 'success', message: `Report sent to ${result.recipients?.length || 0} recipient(s)` });
      setTimeout(() => setEmailFeedback(null), 5000);
    } catch (err) {
      const msg = err.message || 'Failed to send email report';
      setEmailFeedback({ type: 'error', message: msg });
      setTimeout(() => setEmailFeedback(null), 5000);
    } finally {
      setSendingEmail(false);
    }
  };

  /** Build the dashboard HTML — shared between copy-to-clipboard and send-email */
  const buildDashboardHtml = () => {
    const allBugs = report.bugDetails || [];

    const workflowTables = WORKFLOW_SECTIONS.map(wf => {
      const bugs = bugsByWorkflow[wf.key] || [];
      if (bugs.length === 0) return '';
      const rows = bugs.map(bug => {
        const link = `${TASKEI_URL}/${bug.shortId}`;
        const priorityColor = bug.priority === 'High' || bug.priority === 'Blocker' ? '#c62828' : bug.priority === 'Medium' ? '#e65100' : '#2e7d32';
        const foundIn = filterMilestone(bug.affectedMilestone);
        const target = filterMilestone(bug.targetedRelease);
        return `<tr>
          <td style="padding:6px 10px;border-bottom:1px solid #eee"><a href="${link}" style="color:#1565c0;text-decoration:none;font-weight:500">${escapeHtml(bug.title || bug.shortId)}</a></td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee;color:${priorityColor};font-weight:600">${escapeHtml(bug.priority || 'Unset')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.module || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.assigned || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.teams || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(foundIn)}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(target)}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${bug.leadDays ? bug.leadDays + 'd' : '—'}</td>
        </tr>`;
      }).join('');

      return `
        <h3 style="margin:20px 0 4px;padding-left:10px;border-left:4px solid ${wf.color};font-size:14px">${wf.label} (${bugs.length})</h3>
        <p style="margin:0 0 8px;font-size:11px;color:#666;padding-left:14px"><em>${wf.description}</em></p>
        <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:16px">
          <thead>
            <tr style="background:#f5f5f5">
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Title</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Priority</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Module</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Assigned</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Teams</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Found in Milestone</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Targeted Milestone</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Lead</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    }).join('');

    const teamCounts = {};
    allBugs.forEach(bug => {
      const teams = (bug.teams || '').split(',').map(t => t.trim()).filter(Boolean);
      const prio = bug.priority || 'Unset';
      const targets = teams.length > 0 ? teams : ['Unassigned'];
      targets.forEach(t => {
        if (!teamCounts[t]) teamCounts[t] = { High: 0, Medium: 0, Low: 0 };
        if (prio === 'High' || prio === 'Blocker') teamCounts[t].High++;
        else if (prio === 'Medium') teamCounts[t].Medium++;
        else teamCounts[t].Low++;
      });
    });
    const teamRows = Object.entries(teamCounts)
      .sort((a, b) => (b[1].High + b[1].Medium + b[1].Low) - (a[1].High + a[1].Medium + a[1].Low))
      .map(([team, c]) => `<tr><td style="padding:6px 10px;border-bottom:1px solid #eee;font-weight:600">${escapeHtml(team)}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;color:#c62828">${c.High}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;color:#e65100">${c.Medium}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;color:#2e7d32">${c.Low}</td><td style="padding:6px 10px;border-bottom:1px solid #eee;font-weight:700">${c.High + c.Medium + c.Low}</td></tr>`)
      .join('');

    return `
      <div style="font-family:Arial,sans-serif;max-width:1400px;margin:0 auto;padding:20px">
        <h2 style="margin:0 0 4px;color:#333">Bug Dashboard Report</h2>
        <p style="color:#666;margin:0 0 16px">Date: ${selectedDate} | Total Open Bugs: ${allBugs.length}</p>
        <h3 style="margin:0 0 8px;padding-left:10px;border-left:4px solid #333;font-size:14px">Bugs by Team (${allBugs.length} total)</h3>
        <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px">
          <thead><tr style="background:#f5f5f5">
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Team</th>
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">High</th>
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Medium</th>
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Low</th>
            <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Total</th>
          </tr></thead>
          <tbody>${teamRows}</tbody>
        </table>
        ${workflowTables}
        <hr style="margin:24px 0;border:none;border-top:1px solid #eee" />
        <p style="font-size:11px;color:#999">This report was sent from the QA Test Case Review Portal.</p>
      </div>
    `;
  };

  const handleCopyToClipboard = async () => {
    if (!report?.available) return;
    setCopied(false);
    try {
      const html = buildDashboardHtml();
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      alert('Failed to copy to clipboard. Your browser may not support this feature.');
    }
  };

  const totalOpen = report?.bugDetails?.length || 0;
  const minDate = availableDates.length ? availableDates[availableDates.length - 1] : undefined;

  return (
    <div className="bug-dashboard">
      <button className="btn btn-secondary btn-sm back-to-dashboards" onClick={() => navigate('/dashboards')}><ArrowLeft size={14} /> Back to Dashboards</button>
      <div className="bug-dashboard-header">
        <div>
          <h2><Bug size={22} /> Bug Dashboard</h2>
          <p className="bug-dashboard-subtitle">Open defects grouped by workflow status. Total: {totalOpen} bugs.</p>
        </div>
        <div className="bug-dashboard-controls">
          <label className="tr-date"><CalendarDays size={16} /><input type="date" value={selectedDate} min={minDate} max={today()} onChange={(e) => setSelectedDate(e.target.value)} className="input" /></label>
          <button className="btn btn-secondary btn-sm" onClick={() => setRefreshKey(k => k + 1)} disabled={loading}><RefreshCw size={14} /> Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={handleCopyToClipboard} disabled={loading || !report?.available} title="Copy dashboard as HTML to clipboard"><ClipboardCopy size={14} /> {copied ? '✓ Copied!' : 'Copy to Clipboard'}</button>
          <button className="btn btn-secondary btn-sm" onClick={handleSendEmailReport} disabled={loading || !report?.available || sendingEmail} title="Email recipients are configured in the Settings page"><Mail size={14} /> {sendingEmail ? 'Sending...' : 'Send Email Report'}</button>
        </div>
      </div>
      {emailFeedback && (
        <div className={`bug-dashboard-email-feedback ${emailFeedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`} role="status">
          {emailFeedback.message}
        </div>
      )}

      {loading && <div className="bug-dashboard-loading">Loading bug data...</div>}

      {!loading && !report?.available && (
        <div className="bug-dashboard-placeholder"><p>No bug data available for {selectedDate}.</p></div>
      )}

      {!loading && report?.available && (
        <div className="bug-tables-container">
          {/* Team Summary Table */}
          <div className="bug-table-section">
            <h3 className="bug-table-title" style={{ borderLeftColor: '#333' }}>Bugs by Team <span className="bug-table-count">({totalOpen} total)</span></h3>
            <div className="bug-table-wrapper">
              <table className="bug-table">
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>High</th>
                    <th>Medium</th>
                    <th>Low</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const teamCounts = {};
                    (report.bugDetails || []).forEach(bug => {
                      const teams = (bug.teams || '').split(',').map(t => t.trim()).filter(Boolean);
                      const prio = bug.priority || 'Unset';
                      if (teams.length === 0) {
                        if (!teamCounts['Unassigned']) teamCounts['Unassigned'] = { High: 0, Medium: 0, Low: 0 };
                        if (prio === 'High' || prio === 'Blocker') teamCounts['Unassigned'].High++;
                        else if (prio === 'Medium') teamCounts['Unassigned'].Medium++;
                        else teamCounts['Unassigned'].Low++;
                      } else {
                        teams.forEach(t => {
                          if (!teamCounts[t]) teamCounts[t] = { High: 0, Medium: 0, Low: 0 };
                          if (prio === 'High' || prio === 'Blocker') teamCounts[t].High++;
                          else if (prio === 'Medium') teamCounts[t].Medium++;
                          else teamCounts[t].Low++;
                        });
                      }
                    });
                    return Object.entries(teamCounts)
                      .sort((a, b) => (b[1].High + b[1].Medium + b[1].Low) - (a[1].High + a[1].Medium + a[1].Low))
                      .map(([team, counts]) => (
                        <tr key={team}>
                          <td><strong>{team}</strong></td>
                          <td><span className="bug-priority-badge priority-high">{counts.High}</span></td>
                          <td><span className="bug-priority-badge priority-medium">{counts.Medium}</span></td>
                          <td><span className="bug-priority-badge priority-low">{counts.Low}</span></td>
                          <td><strong>{counts.High + counts.Medium + counts.Low}</strong></td>
                        </tr>
                      ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {WORKFLOW_SECTIONS.map(wf => (
            <BugTable
              key={wf.key}
              bugs={bugsByWorkflow[wf.key]}
              workflowLabel={wf.label}
              color={wf.color}
              description={wf.description}
              owner={wf.owner}
              nextAction={wf.nextAction}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default BugDashboard;
